import { APIService } from 'src/app/services/api.service';
import { Injectable } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Router } from '@angular/router';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NzI18nInterface, NzI18nService } from 'ng-zorro-antd/i18n';
import { LoggingService } from './logging.service';
import { BehaviorSubject, filter, Subject } from 'rxjs';
import { formatNumber } from '@angular/common';

export interface TouchedListenerAbstractControl extends AbstractControl {
  touchedChanges: Subject<string>;
}
@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  public globalLocalizedLabels = {
    DELETE: $localize`Delete`,
    DISSOCIATE: $localize`Dissociate`,
    VIEW_PROGRAM_MAP_SUMMARY: $localize`View Program Map summary`,
    VIEW_GEOGRAPHIC_ELEMENT_SUMMARY: $localize`View Geographic Element summary`,
    REMOVE: $localize`Remove`,
    VIEW_FORM_SUMMARY: $localize`View Form summary`,
    TRANSLATE: $localize`Translate`,
    VIEW_WORKFLOW_MESSAGES: $localize`View Workflow messages`,
    CREATE_A_COPY: $localize`Create a copy`,
    TITLE: $localize`Title`,
    YES: $localize`Yes`,
    NO: $localize`No`,
  };

  constructor(
    private readonly apiService: APIService,
    private readonly msg: NzMessageService,
    private readonly router: Router,
    private readonly logger: LoggingService,
    private readonly i18n: NzI18nService,
  ) {}

  public createTouchedListenerForm(form: AbstractControl): TouchedListenerAbstractControl {
    if (!form) {
      return form;
    }
    (form as any)._markAsTouched = form.markAsTouched;
    (form as any).touchedChanges = new Subject();
    form.markAsTouched = (opts): void => {
      (form as any)._markAsTouched(opts);
      (form as any).touchedChanges.next('touched');
    };
    return form as TouchedListenerAbstractControl;
  }
  public getStatusAsNumber(status: string): number {
    if (status === 'one') {
      return 1;
    } else if (status === 'two') {
      return 2;
    } else if (status === 'three') {
      return 3;
    } else if (status === 'four') {
      return 4;
    } else if (status === 'five') {
      return 5;
    } else if (status === 'six') {
      return 6;
    } else if (status === 'seven') {
      return 7;
    } else if (status === 'eight') {
      return 8;
    } else if (status === 'nine') {
      return 9;
    } else if (status === 'ten') {
      return 10;
    }
    return NaN;
  }

  public getStatusAsString(status: number): string {
    if (status === 1) {
      return 'one';
    } else if (status === 2) {
      return 'two';
    } else if (status === 3) {
      return 'three';
    } else if (status === 4) {
      return 'four';
    } else if (status === 5) {
      return 'five';
    } else if (status === 6) {
      return 'six';
    } else if (status === 7) {
      return 'seven';
    } else if (status === 8) {
      return 'eight';
    } else if (status === 9) {
      return 'nine';
    } else if (status === 10) {
      return 'ten';
    }
    return '';
  }

  public async redirectToForm(
    formDefId: string | undefined,
    dynamicFormId: string | undefined,
    dynamicFormName: string | undefined,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    queryParams: any,
  ): Promise<void> {
    try {
      await this.apiService.getOnlineOfflineDynamicFormItem(dynamicFormName as string, dynamicFormId as string);
      if (this.router.url.indexOf('/dynamic/dynamic-form/') !== -1) {
        await this.router.navigate(['/']);
      }
      await this.router.navigate([`/dynamic/dynamic-form/${formDefId}/${dynamicFormId}`], { queryParams });
    } catch (error) {
      this.msg.error($localize`The Form has been deleted, or you do not have access to it!`, {
        nzDuration: 12000,
      });
    }
  }

  public validateAllFormFields(formGroup: UntypedFormGroup, onlySelf = false): void {
    if (!formGroup) {
      throw Error('validateAllFormFields formGroup cannot be null');
    }
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf });
        if (control.status === 'INVALID') {
          this.logger.warn('control invalid', field, control);
        }
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control, onlySelf);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public getLocalizedLabel(obj: any, propertyName: string, lang = this.locale.locale): string {
    if (!obj || !propertyName) {
      this.logger.warn(`Root object and property are mandatory to get localized label`, obj, propertyName);
      return '';
    }
    if (lang === 'en') {
      // by default english properties are not postfixed with lang
      if (!Object.prototype.hasOwnProperty.call(obj, propertyName)) {
        //this.logger.error('Object does not support property ' + propertyName);
        return obj[propertyName];
      }
      return obj[propertyName];
    } else if (lang === 'es' || lang === 'fr') {
      if (!Object.prototype.hasOwnProperty.call(obj, `${propertyName}_${lang}`)) {
        //this.logger.error(`Object does not support property ${propertyName}_${lang}`);
        return obj[propertyName];
      }
      return obj[`${propertyName}_${lang}`];
    }
    throw new Error('Unsupported language ' + lang);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public getLocalizedLabelWithFallback(obj: any, propertyName: string): string {
    if (!obj || !propertyName) {
      this.logger.warn(`Root object and property are mandatory to get localized label`, obj, propertyName);
    }
    const label_en = this.getLocalizedLabel(obj, propertyName, 'en');
    const label_es = this.getLocalizedLabel(obj, propertyName, 'es');
    const label_fr = this.getLocalizedLabel(obj, propertyName, 'fr');

    if (this.locale.locale === 'en' && label_en) {
      return label_en;
    } else if (this.locale.locale === 'es' && label_es) {
      return label_es;
    } else if (this.locale.locale === 'fr' && label_fr) {
      return label_fr;
    } else {
      return label_en || label_es || label_fr || '';
    }
  }

  public noFormatter = (value: number): string => `${value}`;
  public noParser = (value: string): string => value;

  public formatterPercent = (value: number): string => {
    return value || value === 0 ? `${value} %` : '';
  };

  public parserPercent = (value: string): string => {
    value = value.replace(' %', '');
    return value.replace('%', '');
  };

  public parserNoSpace = (value: string): string => {
    return value.replace(' ', '');
  };

  public set innerScreenWidth(value: number) {
    this.innerScreenWidthSubject.next(value);
  }

  public reloadApp(): void {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        Promise.all(registrations.map((r) => r.unregister()))
          .then(() => {
            if (navigator.onLine) {
              window.location.reload();
            }
          })
          .catch((error) => {
            this.logger.error(error);
            if (navigator.onLine) {
              window.location.reload();
            }
          });
      })
      .catch((error) => {
        this.logger.error(error);
        if (navigator.onLine) {
          window.location.reload();
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public jsonAreEqual(objectA: any, objectB: any): boolean {
    let flag = true;

    if (Object.keys(objectA).length === Object.keys(objectB).length) {
      for (const key in objectA) {
        if (objectA[key] === objectB[key]) {
          continue;
        } else {
          flag = false;
          break;
        }
      }
    } else {
      flag = false;
    }

    return flag;
  }

  private readonly innerScreenWidthSubject = new BehaviorSubject<number>(0);

  public locale: NzI18nInterface = this.i18n.getLocale();
  public innerScreenWidth$ = this.innerScreenWidthSubject.asObservable().pipe(filter((width) => width !== 0));
  public readonly emailPattern = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';
  public readonly passwordPattern = '^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z].*[a-z].*[a-z]).{8}$';
  public readonly phoneNumberPattern = '\\(?\\d+\\)?[-.\\s]?\\d+[-.\\s]?\\d+';
  public readonly numberPattern = '^[0-9]*$';
  public readonly urlPattern =
    '^(?:([A-Za-z]+):)?(\\/{0,3})([0-9.\\-A-Za-z]+)(?::(\\d+))?(?:\\/([^?#]*))?(?:\\?([^#]*))?(?:#(.*))?$';
  public readonly formatCurrency = (value: number): string => `${formatNumber(value, this.locale.locale, '1.2-2')}`;
  public readonly formatHa = (value: number): string => `${formatNumber(value, this.locale.locale, '1.0-2')}`;
}
